import { useState, useCallback } from 'react';
import {
  isCounterStrikeTargetingActive,
  isOpponentResolvingCounterStrike,
} from './counterStrikeResolution';

/**
 * Custom hook for managing Counter Strike state and actions
 */
export const useCounterStrike = (ws, gameId, userId, getActiveWebSocket) => {
  const [counterStrikeOrders, setCounterStrikeOrders] = useState([]);
  const [opponentHasCounterStrike, setOpponentHasCounterStrike] = useState(false);
  // ✅ FIX: Track which counterstrike is currently being processed to prevent overwriting
  const [processingCounterStrikeId, setProcessingCounterStrikeId] = useState(null);

  /**
   * Update Counter Strike orders from game state
   * ✅ CRITICAL: Only show counterstrikes when NOT in targeting mode
   * This ensures sequential processing: first counterstrike must complete (including targeting) before second is shown
   */
  const updateCounterStrikeOrders = useCallback((gameState) => {
    if (!gameState || !gameState.state || !gameState.state[userId]) {
      setCounterStrikeOrders([]);
      setOpponentHasCounterStrike(false);
      setProcessingCounterStrikeId(null);
      return;
    }

    const playerState = gameState.state[userId];
    const pendingOrders = playerState.pendingCounterStrike || [];
    
    // ✅ CRITICAL: Check if player is in counterstrike targeting mode
    // Counterstrike strategems: needsStrategemTarget with effectiveCost === 0
    // Counterstrike units: deployTargetMode (during opponent's turn = counterstrike targeting)
    const isInCounterStrikeTargeting = isCounterStrikeTargetingActive(playerState);
    const isCounterStrikeStrategemTargeting =
      isInCounterStrikeTargeting &&
      playerState.needsStrategemTarget &&
      (playerState.needsStrategemTarget.isCounterStrikeStrategem === true ||
        (playerState.hand || []).some(
          (order) =>
            order &&
            String(order.instanceId) ===
              String(playerState.needsStrategemTarget.strategemInstanceId) &&
            (order.isCounterStrikeStrategem === true || order.isCounterStrike === true)
        ));
    const isCounterStrikeDeployTargeting =
      isInCounterStrikeTargeting &&
      playerState.deployTargetMode &&
      playerState.selectedDeployInstanceId;
    
    // ✅ CRITICAL: If player is in targeting mode, don't show any counterstrikes
    // Wait until targeting is complete before showing the next one
    if (isInCounterStrikeTargeting) {
      console.log(`⚡ Counter Strike: Player is in targeting mode - hiding all counterstrikes until targeting completes`);
      if (isCounterStrikeStrategemTargeting) {
        console.log(`⚡ Counter Strike: In strategem targeting for ${playerState.needsStrategemTarget.strategemName}`);
      }
      if (isCounterStrikeDeployTargeting) {
        console.log(`⚡ Counter Strike: In unit deploy targeting`);
      }
      // Clear counterstrike orders while in targeting - don't show any until targeting completes
      setCounterStrikeOrders([]);
      // Keep processingCounterStrikeId set if it was set, so we know we're still processing
      // Don't clear it here - it will be cleared when targeting completes
    } else {
      // Not in targeting mode - can show counterstrikes
      // ✅ CRITICAL: If we were in targeting mode but now we're not, the counterstrike targeting completed
      // Clear processingCounterStrikeId so we can show the next counterstrike
      if (processingCounterStrikeId) {
        const stillPending = pendingOrders.find(o => o.instanceId === processingCounterStrikeId);
        if (!stillPending) {
          // Current counterstrike was completed (removed from pendingCounterStrike) - clear processing state
          console.log(`⚡ Counter Strike: Processing counterstrike ${processingCounterStrikeId} was completed, showing next`);
          setProcessingCounterStrikeId(null);
        } else {
          // Counterstrike is still pending but we're not in targeting - this shouldn't happen normally
          // but if it does, clear processing state to allow showing it
          console.log(`⚡ Counter Strike: Counterstrike ${processingCounterStrikeId} still pending but not in targeting - clearing processing state`);
          setProcessingCounterStrikeId(null);
        }
      }
      
      // Show only the first counterstrike (sequential processing)
      // Only show counterstrikes when not in targeting mode
      if (pendingOrders.length > 0) {
        // Show only the first one - others will be shown after this one completes
        setCounterStrikeOrders([pendingOrders[0]]);
        console.log(`⚡ Counter Strike: Showing first counterstrike (${pendingOrders[0].name}), ${pendingOrders.length - 1} more waiting`);
      } else {
        setCounterStrikeOrders([]);
        // No more counterstrikes - clear processing state
        if (processingCounterStrikeId) {
          console.log(`⚡ Counter Strike: No more pending counterstrikes, clearing processing state`);
          setProcessingCounterStrikeId(null);
        }
      }
    }

    // Block active player while opponent resolves counter strike (pending or CS targeting)
    if (gameState.players && gameState.state) {
      const opponentId = gameState.players.find(id => id !== userId);
      if (opponentId && gameState.state[opponentId]) {
        const opponentState = gameState.state[opponentId];
        const opponentResolving = isOpponentResolvingCounterStrike(gameState, userId);
        setOpponentHasCounterStrike(opponentResolving);

        if (opponentResolving) {
          const pendingCount = (opponentState.pendingCounterStrike || []).length;
          if (pendingCount > 0) {
            console.log(`⚡ Opponent resolving counter strike: ${pendingCount} pending order(s)`);
          } else if (opponentState.needsStrategemTarget?.effectiveCost === 0) {
            console.log(`⚡ Opponent resolving counter-strike strategem targeting: ${opponentState.needsStrategemTarget.strategemName}`);
          } else if (opponentState.deployTargetMode) {
            console.log(`⚡ Opponent resolving counter-strike unit deploy targeting`);
          }
        }
      } else {
        setOpponentHasCounterStrike(false);
      }
    } else {
      setOpponentHasCounterStrike(false);
    }
  }, [userId, processingCounterStrikeId]);

  /**
   * Play a Counter Strike order to the selected zone
   * ✅ FIX: Mark this counterstrike as being processed
   */
  const playCounterStrike = useCallback(async (orderId, zone, instanceId) => {
    const activeWs = getActiveWebSocket?.() ?? ws;
    if (!activeWs || !gameId || !orderId || !zone || !instanceId) {
      console.error('⚡ playCounterStrike: Missing required parameters', { ws: activeWs, gameId, orderId, zone, instanceId });
      return;
    }

    try {
      console.log(`⚡ Playing Counter Strike order ${orderId} to ${zone} zone with instanceId ${instanceId}`);
      
      // ✅ FIX: Mark this counterstrike as being processed to prevent showing next one
      setProcessingCounterStrikeId(instanceId);
      console.log(`⚡ Counter Strike: Marked ${instanceId} as processing`);

      activeWs.send(JSON.stringify({
        type: "play-turn",
        gameId,
        action: {
          type: "playCounterStrike",
          orderId,
          zone,
          instanceId
        }
      }));

      console.log(`⚡ Counter Strike order ${orderId} sent to server`);
      
    } catch (error) {
      console.error('⚡ Error playing Counter Strike order:', error);
      // Clear processing state on error
      setProcessingCounterStrikeId(null);
    }
  }, [ws, gameId, getActiveWebSocket]);

  /**
   * Keep a Counter Strike order (put in hand or destroy if hand is full)
   * ✅ FIX: Mark this counterstrike as being processed
   */
  const keepCounterStrike = useCallback(async (orderId, instanceId) => {
    const activeWs = getActiveWebSocket?.() ?? ws;
    
    if (!activeWs || !gameId || !orderId || !instanceId) {
      console.error('⚡ keepCounterStrike: Missing required parameters', { ws: activeWs, gameId, orderId, instanceId });
      return;
    }

    try {
      console.log(`⚡ Keeping Counter Strike order ${orderId}`);
      
      // ✅ FIX: Mark this counterstrike as being processed to prevent showing next one
      setProcessingCounterStrikeId(instanceId);
      console.log(`⚡ Counter Strike: Marked ${instanceId} as processing (keep)`);

      const message = JSON.stringify({
        type: "play-turn",
        gameId,
        action: {
          type: "keepCounterStrike",
          orderId,
          instanceId
        }
      });
      
      console.log('⚡ Sending keepCounterStrike message:', message);
      activeWs.send(message);

      console.log(`⚡ Keep Counter Strike order ${orderId} sent to server`);
    } catch (error) {
      console.error('⚡ Error keeping Counter Strike order:', error);
      // Clear processing state on error
      setProcessingCounterStrikeId(null);
    }
  }, [ws, gameId, getActiveWebSocket]);

  return {
    counterStrikeOrders,
    opponentHasCounterStrike,
    updateCounterStrikeOrders,
    playCounterStrike,
    keepCounterStrike,
    processingCounterStrikeId
  };
};

export default useCounterStrike;

